import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { pluralize } from '@/shared/lib/plural';
import { Button } from '@/shared/ui/Button';

import styles from './DataTable.module.scss';

export interface DataTableEmptyProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  /** Кнопка, которая выводит из пустоты: «Создать сайт», «Сбросить фильтры». */
  action?: ReactNode;
}

/**
 * Пустое место таблицы.
 *
 * Пустых состояний два, и подменять их одним нельзя. «Ещё ни одного сайта»
 * — это приглашение создать первый. «Ничего не нашлось» — это сообщение о
 * том, что фильтры слишком узкие, и выход отсюда прямо противоположный:
 * сбросить их. Один текст на оба случая всегда врёт в одном из них.
 */
export function DataTableEmpty({ icon, title, description, action }: DataTableEmptyProps) {
  return (
    <div className={styles['empty']}>
      {icon !== undefined && (
        <span className={styles['empty__icon']} aria-hidden="true">
          {icon}
        </span>
      )}

      <p className={styles['empty__title']}>{title}</p>

      {description !== undefined && <p className={styles['empty__text']}>{description}</p>}

      {action !== undefined && <div className={styles['empty__action']}>{action}</div>}
    </div>
  );
}

export interface DataTableSelectionProps {
  count: number;
  onClear: () => void;
  /** Действия над выбранными строками. */
  children?: ReactNode;
}

/**
 * Полоса действий над выбранными строками.
 *
 * Появляется на месте панели фильтров, а не поверх таблицы: всплывающая
 * полоса закрывает последнюю строку — ровно ту, которую только что отметили.
 */
export function DataTableSelection({ count, onClear, children }: DataTableSelectionProps) {
  if (count === 0) {
    return null;
  }

  return (
    <div className={styles['selection']}>
      {/* Количество меняется без перезагрузки экрана, поэтому объявляется
          отдельно — иначе для скринридера выбор происходит беззвучно. */}
      <p className={styles['selection__count']} aria-live="polite">
        Выбрано {pluralize(count, ['строка', 'строки', 'строк'])}
      </p>

      <div className={styles['selection__actions']}>{children}</div>

      <Button variant="ghost" tone="neutral" size="sm" iconLeft={<X size={14} />} onClick={onClear}>
        Снять
      </Button>
    </div>
  );
}
